/* =========================================================================
   BONUS WHEEL — 9 slots: cents, BINGO ($2,000 jackpot), the special ☕ Holy
   Coffee, and (v1.1.0) three ITEM slots — 🎟️ Arcade Ticket, 📿 Rosary Bead,
   ⚱️ Relic Token — which grant to the local inventory instead of cents.
   ========================================================================= */
import { byId } from '../dom';
import { show } from '../dom';
import { ctx } from '../context';
import { fmtCents, fmtDelta } from '../engine';
import { sndTick, sndCorrect, sndJackpot, applause, sndPour, sndAhh } from '../audio';
import { triggerSneeze, triggerCoffee, SNEEZE_CHANCE } from '../overlays';
import { grantItem } from '../inventory';
import { postBreakResult, isAuthenticated } from '../api';
import { currentServerSession } from '../gameController';
import { getLine } from '../characters/registry';
import { triggerCharPop } from '../characters/ui';
import { maybeShowTutorial, tutorialInfoButtonHTML } from '../tutorialOverlay';
import { icon, type IconName } from '../ui/icons';

/* Wedge palette — brand tokens (brand/STYLE-GUIDE.md). Values are unchanged
   from the casino restyle except the old purple relic badge, which is now a
   teal from the TEAL family (no purple in the light theme). */
const TEAL = '#1D5C56'; // --teal (mid): relic-token badge
const TEAL_DEEP = '#163B36'; // --teal-deep: alternate wedge fill
const TEAL_WEDGE = '#1D4A45'; // wedge fill between TEAL_DEEP wedges
const GOLD_INK = '#7A5A10'; // deep gold: the BINGO wedge
const GOLD = '#F5B914'; // --gold: BINGO badge
const GOLD_TEXT = '#FFD95A'; // BINGO label ink on the gold wedge
const MARIAN = '#3B76C9'; // Marian blue badge
const WINE = '#C94F4F'; // wine-red badge
const ROSE = '#D96A8B'; // arcade-ticket badge
const SAGE = '#2E9E6B'; // rosary-bead badge
const COFFEE = '#8A5A3B'; // Holy Coffee badge
const INK_WHITE = '#fff';

/** A wheel item grant (v1.1.0). The WheelIndex of an item slot is a CROSS-AGENT
    contract mirrored server-side (db/015): 2→arcade_ticket, 4→rosary_bead,
    8→relic_token. Display metadata for the result card lives in ITEM_META. */
export interface WheelItem {
  type: string;
  qty: number;
}

export interface WheelSlot {
  label: string;
  cents: number;
  color: string;
  text: string;
  weight: number;
  jackpot?: boolean;
  coffee?: boolean;
  /** Item slots grant to inventory instead of cents (no cents awarded). */
  item?: WheelItem;
  /** Presentational: fill of the colored icon-badge chip on the wedge. */
  badge?: string;
}

/** Display name for each item type shown on the wheel result card. */
const ITEM_META: Record<string, { name: string; icon: IconName }> = {
  arcade_ticket: { name: 'Arcade Ticket', icon: 'ticket' },
  rosary_bead: { name: 'Rosary Bead', icon: 'rosary' },
  relic_token: { name: 'Relic Token', icon: 'urn' },
};

/* Casino restyle: wedges alternate two deep teals so the colored badge chips
   carry the slot identity; BINGO alone gets a deep-gold wedge. Weights/cents/
   order are UNCHANGED (indices 2/4/8 are the db/015 item-slot contract). */
export const WHEEL: WheelSlot[] = [
  { label: '$500', cents: 50_000, color: TEAL_WEDGE, text: INK_WHITE, weight: 3, badge: WINE },
  { label: '$1,000', cents: 100_000, color: TEAL_DEEP, text: INK_WHITE, weight: 2, badge: MARIAN },
  { label: '🎟️', cents: 0, color: TEAL_WEDGE, text: INK_WHITE, weight: 4, item: { type: 'arcade_ticket', qty: 1 }, badge: ROSE },
  { label: 'BINGO', cents: 200_000, color: GOLD_INK, text: GOLD_TEXT, weight: 1, jackpot: true, badge: GOLD },
  { label: '📿', cents: 0, color: TEAL_DEEP, text: INK_WHITE, weight: 3, item: { type: 'rosary_bead', qty: 1 }, badge: SAGE },
  { label: '☕', cents: 15_000, color: TEAL_WEDGE, text: INK_WHITE, weight: 2, coffee: true, badge: COFFEE },
  { label: '$750', cents: 75_000, color: TEAL_DEEP, text: INK_WHITE, weight: 2, badge: WINE },
  { label: '$100', cents: 10_000, color: TEAL_WEDGE, text: INK_WHITE, weight: 4, badge: MARIAN },
  { label: '⚱️', cents: 0, color: TEAL_DEEP, text: INK_WHITE, weight: 2, item: { type: 'relic_token', qty: 1 }, badge: TEAL },
];
const SEG = WHEEL.length;
const SEG_DEG = 360 / SEG;

/** 18 marquee bulbs sitting ON the gold rim (r=139), alternating blink phase. */
const RIM_BULBS = Array.from({ length: 18 }, (_, i) => {
  const p = polar(150, 150, 139, i * 20);
  return `<circle class="wheel-bulb ${i % 2 ? 'even' : 'odd'}" cx="${p.x.toFixed(2)}" cy="${p.y.toFixed(2)}" r="4" fill="#FFF6DC"/>`;
}).join('');

/* The wheel is one SVG: #wheelSpin (wedges, rebuilt by buildWheel) rotates;
   #wheelChrome (gold rim + bulbs + hub + pointer) is STATIC and must stay
   OUTSIDE #wheelSpin — buildWheel only rewrites #wheelSpin's innerHTML. */
export const bonusScreenHTML = /* html */ `
<div id="screenBonus" class="bonus hidden">
  <div class="kicker">Halfway Bonus${tutorialInfoButtonHTML('wheel')}</div>
  <h2>Bonus Spin</h2>
  <div class="blurb">Free points, no risk. Tap <b>Spin</b> and hope for <b>BINGO</b> ($2,000).</div>
  <div class="wheel-wrap">
    <svg id="wheelSVG" viewBox="0 0 300 300">
      <defs>
        <linearGradient id="wheelRimGold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#FFD95A"/><stop offset="100%" stop-color="#DE9F00"/>
        </linearGradient>
        <radialGradient id="wheelHubDome" cx="38%" cy="32%" r="85%">
          <stop offset="0%" stop-color="#2C7A6E"/><stop offset="100%" stop-color="#12332F"/>
        </radialGradient>
      </defs>
      <g id="wheelSpin"></g>
      <g id="wheelChrome">
        <circle cx="150" cy="150" r="132.5" fill="none" stroke="rgba(0,0,0,0.22)" stroke-width="1"/>
        <circle cx="150" cy="150" r="139" fill="none" stroke="url(#wheelRimGold)" stroke-width="13"/>
        <circle cx="150" cy="150" r="145.5" fill="none" stroke="rgba(0,0,0,0.22)" stroke-width="1"/>
        ${RIM_BULBS}
        <path d="M150 92 C151.5 102 157 117 159 130 L141 130 C143 117 148.5 102 150 92 Z" transform="translate(0 2.5)" fill="rgba(0,0,0,0.25)"/>
        <path d="M150 92 C151.5 102 157 117 159 130 L141 130 C143 117 148.5 102 150 92 Z" fill="url(#wheelRimGold)" stroke="rgba(0,0,0,0.25)" stroke-width="1"/>
        <circle cx="150" cy="150" r="27" fill="#163B36" stroke="#F5B914" stroke-width="2.5"/>
        <circle cx="150" cy="150" r="21" fill="url(#wheelHubDome)"/>
        <circle cx="150" cy="150" r="4" fill="#0C2320"/>
      </g>
    </svg>
  </div>
  <div class="result" id="wheelResult"></div>
  <div class="cta"><button class="btn" id="spinBtn">Spin</button></div>
</div>`;

let wheelRotation = 0;
let spinning = false;

export function resetWheel(): void {
  wheelRotation = 0;
  // Also reset the element itself (safe while the wheel screen is hidden — no
  // transition runs) and re-orient labels for the zero rest position; without
  // this the DOM kept the last game's rotation and could visibly unwind.
  const el = document.getElementById('wheelSpin');
  if (el) {
    el.style.transform = 'rotate(0deg)';
    buildWheel(0);
  }
}

function polar(cx: number, cy: number, r: number, angDegFromTop: number): { x: number; y: number } {
  const t = ((angDegFromTop - 90) * Math.PI) / 180; // 0 = top
  return { x: cx + r * Math.cos(t), y: cy + r * Math.sin(t) };
}

/** Draw the wheel with labels oriented for a given RESTING rotation. The wheel
    spins as one group, so "which labels are on the bottom half" depends on where
    it stops — callers pass the rest angle (0 at boot; the landing angle at spin
    start, when mid-spin motion hides the re-orientation). */
export function buildWheel(restDeg = 0): void {
  const cx = 150,
    cy = 150,
    r = 132; // wedges stop at 132 — the static gold rim (r≈139 in the template) rides outside them
  const FONT = `font-family="ui-rounded,'SF Pro Rounded',system-ui,sans-serif"`;
  let svg = '';
  WHEEL.forEach((seg, i) => {
    const a0 = i * SEG_DEG,
      a1 = a0 + SEG_DEG;
    const p0 = polar(cx, cy, r, a0),
      p1 = polar(cx, cy, r, a1);
    // Item slots (🎟️/📿/⚱️) carry a class so the wheel tutorial can spotlight
    // exactly those three wedges, not the whole wheel — see TUTORIALS.wheel[1].
    const itemCls = seg.item ? ' class="wheel-seg-item"' : '';
    svg += `<path d="M${cx} ${cy} L${p0.x.toFixed(2)} ${p0.y.toFixed(2)} A${r} ${r} 0 0 1 ${p1.x.toFixed(2)} ${p1.y.toFixed(2)} Z" fill="${seg.color}" stroke="rgba(255,255,255,0.06)" stroke-width="1"${itemCls}/>`;
    const mid = a0 + SEG_DEG / 2;
    // Glyphs that END UP on the lower half at rest would render upside-down;
    // flip those 180° so everything reads correctly where the wheel stops.
    // The test uses each glyph's own resting position (mid + restDeg).
    const restMid = (((mid + restDeg) % 360) + 360) % 360;
    const flip = restMid > 90 && restMid < 270 ? 180 : 0;
    const rotAt = (p: { x: number; y: number }): string =>
      `transform="rotate(${(mid + flip).toFixed(2)} ${p.x.toFixed(2)} ${p.y.toFixed(2)})"`;
    // Colored badge chip near the rim (0.78r) carrying the slot's glyph:
    // item/coffee slots show their emoji; cash slots a '$'; BINGO a star.
    const bp = polar(cx, cy, r * 0.78, mid);
    const emoji = /[^\x00-\x7F]/.test(seg.label);
    const glyph = emoji ? seg.label : seg.jackpot ? '★' : '$';
    const glyphFill = emoji ? '#fff' : seg.jackpot ? '#3A2705' : '#fff';
    svg += `<circle cx="${bp.x.toFixed(2)}" cy="${bp.y.toFixed(2)}" r="16" fill="${seg.badge || seg.color}" stroke="rgba(0,0,0,0.18)" stroke-width="2"/>`;
    svg += `<text x="${bp.x.toFixed(2)}" y="${bp.y.toFixed(2)}" fill="${glyphFill}" ${FONT} font-size="${emoji ? 18 : 16}" font-weight="800" text-anchor="middle" dominant-baseline="central" ${rotAt(bp)}>${glyph}</text>`;
    // Cash + BINGO slots also carry their value text between hub and badge,
    // oriented ALONG the radius (like a real prize wheel) so long labels never
    // crowd the narrow center. Reads outward on the resting right half, flipped
    // on the left half so it's never upside-down at rest. Emoji slots' label IS
    // the badge glyph — never render it twice.
    if (!emoji) {
      const lp = polar(cx, cy, r * 0.5, mid);
      const radial = restMid <= 180 ? mid - 90 : mid + 90;
      const vfont = seg.label.length >= 6 ? 13.5 : 15;
      svg += `<text x="${lp.x.toFixed(2)}" y="${lp.y.toFixed(2)}" fill="${seg.text}" ${FONT} font-size="${vfont}" font-weight="800" letter-spacing="0.5" text-anchor="middle" dominant-baseline="central" transform="rotate(${radial.toFixed(2)} ${lp.x.toFixed(2)} ${lp.y.toFixed(2)})">${seg.label}</text>`;
    }
  });
  byId('wheelSpin').innerHTML = svg;
}

export function pickSegment(): number {
  const total = WHEEL.reduce((s, w) => s + w.weight, 0);
  let roll = Math.random() * total;
  for (let i = 0; i < WHEEL.length; i++) {
    roll -= WHEEL[i].weight;
    if (roll <= 0) return i;
  }
  return WHEEL.length - 1;
}

export function showBonusWheel(): void {
  // Self-prime every time the wheel is shown. The post-game Arcade already calls
  // resetWheel() right before this, which is why it spins on ONE tap; the in-game
  // break path (runBreak → showBonusWheel) did not, so the first spin's transition
  // could fail to start from a just-unhidden screen — the wheel jumped a segment,
  // transitionend never fired, and a second tap was needed (QA bug #4). Priming
  // here (transform reset + buildWheel reflow) makes both paths behave identically.
  resetWheel();
  const spinBtn = byId<HTMLButtonElement>('spinBtn');
  byId('wheelResult').innerHTML = '';
  spinBtn.disabled = false;
  spinBtn.textContent = 'Spin';
  spinBtn.onclick = spinWheel; // reset handler cleanly each time the wheel opens
  spinning = false;
  show('bonus');
  maybeShowTutorial('wheel'); // first encounter only (break AND arcade paths)
}

export function spinWheel(): void {
  if (spinning) return;
  spinning = true;
  const spinBtn = byId<HTMLButtonElement>('spinBtn');
  const wheelSpin = byId('wheelSpin');
  const wheelResult = byId('wheelResult');
  spinBtn.disabled = true;
  const i = pickSegment();
  const seg = WHEEL[i];
  const mid = i * SEG_DEG + SEG_DEG / 2; // segment center, clockwise from top
  const jitter = (Math.random() * 2 - 1) * (SEG_DEG * 0.35);
  const targetMod = (((-mid + jitter) % 360) + 360) % 360; // rotation that lands mid under top pointer
  let final = wheelRotation - (wheelRotation % 360) + targetMod;
  while (final < wheelRotation + 360 * 5) final += 360;
  wheelRotation = final;
  // Re-orient labels for where the wheel will STOP (targetMod). Doing it at spin
  // start is invisible — the text is motion-blurred the moment the spin begins.
  buildWheel(targetMod);
  wheelSpin.style.transform = `rotate(${final}deg)`;
  // tick sounds during spin
  let ticks = 0;
  const tickIv = setInterval(() => {
    sndTick();
    if (++ticks > 22) clearInterval(tickIv);
  }, 170);
  const enableContinue = (): void => {
    spinBtn.textContent = 'Continue';
    spinBtn.disabled = false;
    spinBtn.onclick = () => {
      spinBtn.onclick = spinWheel;
      ctx.nextQuestion();
    };
  };
  const finish = (): void => {
    clearInterval(tickIv);

    // 🎟️/📿/⚱️ Item slot (v1.1.0): grants an inventory item, NO cents. Kept fully
    // functional offline/guest via the local inventory. In the post-game Arcade
    // SANDBOX we suppress the real grant (ctx.arcadeMode) so the infinitely
    // replayable wheel can't farm items / the relic quest.
    //
    // AUTHED server grant (enabled after the server zeroed item-slot cents in
    // Core/Content/packs.json — indices 2/4/8 now yield 0 cents, so break-result
    // records the item idempotently without over-crediting coins). Guests and the
    // arcade sandbox never call it. NOTE: the local grant ALWAYS happens (it is the
    // client-side truth for UI/quests); the server record is parallel bookkeeping.
    // If server→client inventory reconciliation is ever added, it must max-merge,
    // not sum, or wheel items would double-count.
    if (seg.item) {
      spinning = false;
      sndCorrect();
      const meta = ITEM_META[seg.item.type] || { name: seg.item.type, icon: 'gift' as IconName };
      if (!ctx.arcadeMode) {
        // Local grant is ALWAYS the client-side truth (drives inventory UI, quests,
        // telemetry — offline-safe). For authed games with a server session we also
        // record the grant server-side; the idempotency key is stable per round so
        // a retried request can't double-record, and usp_Inventory_Grant dedups.
        grantItem(seg.item.type, seg.item.qty, 'wheel'); // → InventoryGranted → quests
        const sessionId = isAuthenticated() ? currentServerSession() : null;
        if (sessionId) {
          void postBreakResult({
            sessionId,
            gameKey: 'wheel',
            wheelIndex: i,
            idempotencyKey: `wheel-item:${sessionId}:r${ctx.game.roundIndex}`,
          }); // fire-and-forget; 503/offline is fine — the local grant already landed
        }
      }
      try {
        triggerCharPop(getLine('theo', 'wheel-item'));
      } catch {
        /* no character UI mounted — skip the pop */
      }
      wheelResult.innerHTML =
        `<div class="r-amt norm">${icon(meta.icon, 26)} ${meta.name}</div>` +
        `<div class="r-sub">A little treasure. Keep it well.</div>`;
      enableContinue();
      return;
    }

    ctx.engine?.awardBonus(seg.cents, seg.coffee ? 'Holy Coffee' : seg.label, seg.coffee ? 'Coffee' : 'Bonus Spin');
    ctx.updateBank();
    ctx.floatDelta(seg.cents);
    spinning = false;

    // ☕ Holy Coffee — special wheel outcome
    if (seg.coffee) {
      ctx.addAchievement('☕', 'Holy Coffee', 'Blessed with the rare cup on the Bonus Wheel');
      sndPour();
      sndAhh();
      wheelResult.innerHTML = `<div class="r-amt norm">Holy Coffee</div><div class="r-sub">A blessed cup. ${fmtDelta(seg.cents)} added.</div>`;
      spinBtn.disabled = true;
      triggerCoffee(seg.cents, enableContinue);
      return;
    }

    if (seg.jackpot) {
      ctx.addAchievement('🎡', 'BINGO!', 'Hit the $2,000 jackpot on the Bonus Wheel');
      sndJackpot();
      applause();
    } else {
      sndCorrect();
    }
    const cls = seg.jackpot ? 'jackpot' : 'norm';
    const headline = seg.jackpot ? 'BINGO!' : fmtDelta(seg.cents);
    const sub = seg.jackpot ? `Jackpot: ${fmtCents(seg.cents)} to your bank` : `Added to your bank`;
    wheelResult.innerHTML = `<div class="r-amt ${cls}">${headline}</div><div class="r-sub">${sub}</div>`;

    // 🤧 Saintly Sneeze — rare random blessing after a (non-jackpot) spin
    if (!seg.jackpot && Math.random() < SNEEZE_CHANCE) {
      spinBtn.disabled = true;
      triggerSneeze(enableContinue);
    } else {
      enableContinue();
    }
  };
  wheelSpin.addEventListener('transitionend', finish, { once: true });
  setTimeout(() => {
    if (spinning) finish();
  }, 4600); // fallback
}
